import React, { useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Modal,
    Spinner,
} from 'react-bootstrap';
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiCloudRain,
    FiPlay,
    FiRefreshCw,
} from 'react-icons/fi';
import { checkStandardTripsForecasts } from '../../services/api';
import { formatRequestError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import { useLongRunningProgress, getLongWaitMessage } from '../../hooks/useLongRunningProgress';
import LongRunningOperationBanner from './LongRunningOperationBanner';
import './StandardCheckForecastTab.css';

const DEFAULT_PREFIX = 'pages.adminTripsDaily.standardCheckForecast';

const parseCheckForecastError = (error, t, prefix) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;

    if (status === 400) {
        const apiMessage = detail && typeof detail === 'object' && detail.message
            ? String(detail.message)
            : (typeof detail === 'string' ? detail : null);
        return {
            title: t(`${prefix}.errors.genericTitle`),
            message: apiMessage || t(`${prefix}.errors.badRequest`),
        };
    }
    if (status === 403) {
        return {
            title: t(`${prefix}.errors.forbiddenTitle`),
            message: t(`${prefix}.errors.forbidden`),
        };
    }
    if (status === 404) {
        const apiMessage = detail && typeof detail === 'object' && detail.message
            ? String(detail.message)
            : (typeof detail === 'string' ? detail : null);
        return {
            title: t(`${prefix}.errors.notFoundTitle`),
            message: apiMessage || t(`${prefix}.errors.notFound`),
        };
    }
    if (status === 500) {
        return {
            title: t(`${prefix}.errors.serverTitle`),
            message: t(`${prefix}.errors.server`),
        };
    }

    if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.message) {
        return {
            title: t(`${prefix}.errors.genericTitle`),
            message: String(detail.message),
        };
    }
    if (typeof detail === 'string' && detail.trim()) {
        return {
            title: t(`${prefix}.errors.genericTitle`),
            message: detail,
        };
    }

    return {
        title: t(`${prefix}.errors.genericTitle`),
        message: formatRequestError(error, t(`${prefix}.error`), {
            timeout: t(`${prefix}.errors.timeout`),
            network: t(`${prefix}.errors.network`),
        }),
    };
};

const buildResultRows = (result, t, prefix) => {
    if (!result || typeof result !== 'object') return [];

    const summary = result.summary && typeof result.summary === 'object' ? result.summary : {};
    const checkClaims = result.check_claims && typeof result.check_claims === 'object'
        ? result.check_claims
        : {};
    const metadata = checkClaims.metadata && typeof checkClaims.metadata === 'object'
        ? checkClaims.metadata
        : {};

    const rows = [];

    if (summary.total_policies_processed != null) {
        rows.push({
            key: 'policiesProcessed',
            label: t(`${prefix}.policiesProcessed`),
            value: summary.total_policies_processed,
        });
    }
    if (summary.policies_triggered != null) {
        rows.push({
            key: 'policiesTriggered',
            label: t(`${prefix}.policiesTriggered`),
            value: summary.policies_triggered,
        });
    }
    if (summary.policies_with_errors != null) {
        rows.push({
            key: 'policiesWithErrors',
            label: t(`${prefix}.policiesWithErrors`),
            value: summary.policies_with_errors,
        });
    }

    const forecastHour = summary.forecast_hour ?? metadata.forecast_hour;
    if (forecastHour != null && forecastHour !== '') {
        rows.push({
            key: 'forecastHour',
            label: t(`${prefix}.forecastHour`),
            value: forecastHour,
        });
    }

    const processingTime = summary.processing_time_ms ?? metadata.processing_time_ms;
    if (processingTime != null) {
        rows.push({
            key: 'processingTime',
            label: t(`${prefix}.processingTime`),
            value: `${Math.round(processingTime)} ms`,
        });
    }

    if (result.timestamp) {
        rows.push({
            key: 'timestamp',
            label: t(`${prefix}.timestamp`),
            value: result.timestamp,
        });
    }

    return rows;
};

const StandardCheckForecastTab = ({
    prefix = DEFAULT_PREFIX,
    runRequest = checkStandardTripsForecasts,
    sourceField = 'forecast_data_source',
}) => {
    const { t } = useTranslation();
    const PREFIX = prefix;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);

    const progressSteps = useMemo(() => ([
        t(`${PREFIX}.progress.step1`),
        t(`${PREFIX}.progress.step2`),
        t(`${PREFIX}.progress.step3`),
    ]), [t, PREFIX]);

    const { currentStep, elapsedSeconds } = useLongRunningProgress(loading, progressSteps);
    const longWaitNote = getLongWaitMessage(elapsedSeconds, {
        stillNormal: t(`${PREFIX}.progress.stillNormal`),
        long: t(`${PREFIX}.progress.longWait`),
        twoMinutes: t(`${PREFIX}.progress.twoMinutesWait`),
        veryLong: t(`${PREFIX}.progress.veryLongWait`),
        overFourMinutes: t(`${PREFIX}.progress.overFourMinutesWait`),
    });

    const isSuccess = result?.status === 'success' || result?.status_code === 200;
    const resultRows = useMemo(() => buildResultRows(result, t, PREFIX), [result, t, PREFIX]);

    const openConfirm = () => {
        if (loading) return;
        setError(null);
        setShowConfirm(true);
    };

    const runCheck = async () => {
        if (loading) return;
        setShowConfirm(false);
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const response = await runRequest();
            setResult(response);
        } catch (err) {
            if (err?.response?.status !== 401) {
                setError(parseCheckForecastError(err, t, PREFIX));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="standard-check-forecast-tab">
            <header className="scf-header">
                <h2 className="scf-title">{t(`${PREFIX}.title`)}</h2>
                <p className="scf-subtitle">{t(`${PREFIX}.description`)}</p>
                <p className="scf-source">
                    <span className="scf-source__label">{t(`${PREFIX}.sourceLabel`)}</span>
                    <code className="scf-source__value">{sourceField}</code>
                </p>
            </header>

            <section className="scf-action">
                <div className="scf-action__info">
                    <div className="scf-action__ready">{t(`${PREFIX}.readyTitle`)}</div>
                </div>
                <Button
                    type="button"
                    className="btn scf-btn-primary"
                    onClick={openConfirm}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner size="sm" animation="border" aria-hidden />
                            {t(`${PREFIX}.running`)}
                        </>
                    ) : (
                        <>
                            <FiPlay size={16} aria-hidden />
                            {t(`${PREFIX}.runButton`)}
                        </>
                    )}
                </Button>
            </section>

            <LongRunningOperationBanner
                show={loading}
                sticky
                title={t(`${PREFIX}.progress.title`)}
                expectedDuration={t(`${PREFIX}.progress.expectedDuration`)}
                message={currentStep}
                elapsedSeconds={elapsedSeconds}
                elapsedLabel={t(`${PREFIX}.progress.elapsed`)}
                longWaitNote={longWaitNote}
                hint={t(`${PREFIX}.progress.hint`)}
            />

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)} className="scf-alert">
                    <div className="d-flex align-items-start gap-2 mb-2">
                        <FiAlertTriangle className="flex-shrink-0 mt-1" aria-hidden />
                        <div>
                            <strong className="d-block mb-1">{error.title}</strong>
                            <span>{error.message}</span>
                        </div>
                    </div>
                    <div className="text-end">
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={runCheck}
                            disabled={loading}
                        >
                            <FiRefreshCw size={14} className="me-1" aria-hidden />
                            {t(`${PREFIX}.retry`)}
                        </Button>
                    </div>
                </Alert>
            )}

            {result && (
                <section className={`scf-result ${isSuccess ? 'is-success' : 'is-warning'}`}>
                    <div className="scf-result__header">
                        {isSuccess
                            ? <FiCheckCircle className="scf-result__icon" aria-hidden />
                            : <FiCloudRain className="scf-result__icon" aria-hidden />}
                        <div>
                            <h3 className="scf-result__title">{t(`${PREFIX}.resultTitle`)}</h3>
                            <p className="scf-result__message mb-0">
                                {isSuccess
                                    ? (result.message || t(`${PREFIX}.successDefault`))
                                    : (result.message || t(`${PREFIX}.resultTitle`))}
                            </p>
                        </div>
                        {result.status && (
                            <Badge bg={isSuccess ? 'success' : 'secondary'} className="scf-result__status">
                                {result.status}
                            </Badge>
                        )}
                    </div>

                    {resultRows.length > 0 && (
                        <ul className="scf-result__list">
                            {resultRows.map((row) => (
                                <li key={row.key} className="scf-result__row">
                                    <FiCheckCircle className="scf-result__row-icon" aria-hidden />
                                    <span className="scf-result__row-label">{row.label}</span>
                                    <span className="scf-result__row-value">{row.value}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="scf-result__footer">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setResult(null)}
                        >
                            {t(`${PREFIX}.clearResult`)}
                        </Button>
                    </div>
                </section>
            )}

            <Modal
                show={showConfirm}
                onHide={() => !loading && setShowConfirm(false)}
                centered
                className="scf-confirm-modal"
            >
                <Modal.Header closeButton={!loading}>
                    <Modal.Title>
                        <FiCloudRain className="me-2" aria-hidden />
                        {t(`${PREFIX}.confirmTitle`)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">{t(`${PREFIX}.confirmMessage`)}</p>
                </Modal.Body>
                <Modal.Footer className="scf-confirm-modal__footer">
                    <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={() => setShowConfirm(false)}
                        disabled={loading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        className="btn scf-btn-primary"
                        onClick={runCheck}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Spinner size="sm" animation="border" aria-hidden />
                                {t(`${PREFIX}.running`)}
                            </>
                        ) : (
                            <>
                                <FiPlay size={16} aria-hidden />
                                {t(`${PREFIX}.confirmRun`)}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StandardCheckForecastTab;
